import { faTire } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { withTranslation } from "react-i18next";
import { Grid } from "semantic-ui-react";

import { ActionButtons, ConditionIcon, Expandable, GalleryButton } from "../../../../../components";
import { DesktopOnly, MobileOnly, Responsive } from "../../../../../layout";
import { ANSWERS } from "../../../../../util/answers";
import { QUESTION_RESULT_STATUS, round2 } from "../../../../../util/common";
import { getTyreItemName, getTyreSeasonIcon } from "../../../../../util/tyre";

import "./index.scss";

const renderPrice = (price, include_vat, vat) => {
  if (!(price > 0.01)) return null;

  if (include_vat) price = price * (1 + vat / 100);

  return <span className="replacement-item-price">€ {round2(price).toFixed(2)}</span>;
};

class ReplacementItems extends React.Component {
  handleUpdateTyreReplacement = (replacementId, answer) => {
    const { readOnly, items, updateItems } = this.props;
    const { ADD_TO_CART, CONTACT_ME, NOT_OK, NOT_ANSWERED } = ANSWERS;

    if (readOnly) return;

    const itemIdx = items.findIndex(t => t.replacements?.some(r => r.id === replacementId));
    if (itemIdx < 0) return;

    const hasSelectedFixOrContact = [ADD_TO_CART, CONTACT_ME].includes(answer);

    const updatedItems = items.with(itemIdx, {
      ...items[itemIdx],
      replacements: items[itemIdx].replacements.map(r => ({
        ...r,
        selected: r.id === replacementId && hasSelectedFixOrContact,
        communication_result_status: r.id === replacementId ? answer : answer === NOT_ANSWERED ? NOT_ANSWERED : NOT_OK,
      })),
    });

    updateItems(updatedItems);
  };

  isNotFixedButtonDisabled = status => {
    const { settings } = this.props;

    switch (status) {
      case QUESTION_RESULT_STATUS.NECESSARY:
        return !settings.enable_not_fixed_critical;
      case QUESTION_RESULT_STATUS.ADVISED:
        return !settings.enable_not_fixed_advised;
      case null:
        return !settings.enable_not_fix_without_status;
      default:
        return true;
    }
  };

  renderTyreItem = item => {
    const { readOnly, settings, highlightReplacementItems, t } = this.props;

    const isHighlighted =
      highlightReplacementItems &&
      item.replacements?.some(r => r.communication_result_status === ANSWERS.NOT_ANSWERED) &&
      (item.question_result_status === QUESTION_RESULT_STATUS.NECESSARY ||
        (item.question_result_status === QUESTION_RESULT_STATUS.ADVISED && !settings.skip_optional_items) ||
        (item.question_result_status === null && !settings.skip_without_status_items));

    return (
      <Grid key={item.id} className={`replacement-item-row ${readOnly ? "read-only" : ""} ${isHighlighted ? "highlight" : ""}`}>
        <Grid.Column computer={14} widescreen={14} mobile={12} className="replacement-item-details-column">
          <div className="replacement-item-title-container">
            <ConditionIcon className="tyre-condition-icon" status={item.question_result_status} />
            <span>{`${getTyreItemName(t, item)} ${item.tyre?.description || ""}`}</span>
          </div>
        </Grid.Column>

        <Grid.Column computer={1} mobile={2} className="replacement-item-gallery-column">
          <div className="replacement-item-gallery">
            <GalleryButton images={item.images} videos={item.videos} />
          </div>
        </Grid.Column>

        {item.replacements?.length > 0 && (
          <Grid divided="vertically" className="replacement-options-section">
            {item.replacements.map(replacement => {
              return (
                <Grid.Row className="replacement-option-row" key={replacement.id}>
                  <Grid.Column computer={5} widescreen={6} mobile={16} className="replacement-option-details-column">
                    <div className="replacement-option-title-container">
                      <div className="replacement-option-title">
                        {getTyreSeasonIcon(replacement.tyre?.season)}
                        <span>{replacement.tyre?.manufacturer}</span>
                      </div>
                      <span className="replacement-option-description">{replacement.tyre?.description}</span>
                    </div>
                  </Grid.Column>

                  <Grid.Column computer={4} mobile={12} className="replacement-option-remark-column">
                    <div className="replacement-option-remark">
                      <Expandable text={replacement.remark} />
                    </div>
                  </Grid.Column>

                  <MobileOnly>
                    <Grid.Column mobile={4} className="replacement-option-price-mobile">
                      {renderPrice(replacement.price, settings.include_vat, settings.vat)}
                    </Grid.Column>
                  </MobileOnly>

                  <DesktopOnly>
                    <Grid.Column mobile={2} computer={1} className="replacement-option-spacer-price" />
                    <Grid.Column computer={2} className="replacement-option-price-desktop">
                      {renderPrice(replacement.price, settings.include_vat, settings.vat)}
                    </Grid.Column>
                  </DesktopOnly>

                  <Grid.Column computer={4} widescreen={3} mobile={16} className="replacement-option-actions-column">
                    <ActionButtons
                      status={replacement.communication_result_status || ANSWERS.NOT_ANSWERED}
                      onAnswer={answer => this.handleUpdateTyreReplacement(replacement.id, answer)}
                      readOnly={readOnly}
                      showNotFixedButton
                      disableNotFixedButton={this.isNotFixedButtonDisabled(item.question_result_status)}
                    />
                  </Grid.Column>
                </Grid.Row>
              );
            })}
          </Grid>
        )}
      </Grid>
    );
  };

  render() {
    const { items, t, showMobile } = this.props;

    if (!items?.length) return null;

    return (
      <Responsive showMobile={showMobile}>
        <Grid className={`tyre-replacements-container component-container ${showMobile ? "show-mobile" : ""}`}>
          <Grid.Row className="tyre-replacements-header-row">
            <div className="tyre-replacements-header">
              <FontAwesomeIcon icon={faTire} className="tyre-replacements-header-icon" />
              <h4 className="tyre-replacements-header-title">{t("cc_tyre_replacements")?.message || "Tyre Replacements"}</h4>
            </div>
          </Grid.Row>

          <Grid.Row className="tyre-replacements-items-rows">{items.map(item => this.renderTyreItem(item))}</Grid.Row>
        </Grid>
      </Responsive>
    );
  }
}

export default withTranslation()(ReplacementItems);
